import { useEffect, useMemo, useRef, useState } from 'react';
import { formatPersonName, useNameFormat } from '@/lib/names';
import { transliterateCyrillic } from '@/lib/transliterate';
import { useI18n } from '@/lib/i18n';
import { Icon } from './Icon';
import type { Person } from '@/types/api';

export function PersonAutocomplete({
  persons,
  value,
  onChange,
  label,
  placeholder,
  hideLabel = false,
}: {
  persons: Person[];
  value: string | null;
  onChange: (personId: string | null) => void;
  label: string;
  placeholder?: string;
  hideLabel?: boolean;
}) {
  const { t, locale } = useI18n();
  const nameFormat = useNameFormat();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = persons.find((person) => person.id === value) ?? null;
  const selectedName = selected ? formatPersonName(selected, nameFormat) : '';

  useEffect(() => {
    if (!open) setQuery(selectedName);
  }, [open, selectedName]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setQuery(selectedName);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedName]);

  const filtered = useMemo(() => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const source = [...persons].sort((a, b) =>
      formatPersonName(a, nameFormat).localeCompare(
        formatPersonName(b, nameFormat),
        locale === 'en' ? 'en' : 'ru',
      ),
    );
    if (tokens.length === 0) return source.slice(0, 30);
    return source
      .filter((person) => {
        const values = [
          person.firstName,
          person.middleName,
          person.lastName,
          person.maidenName,
        ].filter((value): value is string => Boolean(value));
        const haystack = [
          ...values,
          ...values.map(transliterateCyrillic),
        ]
          .join(' ')
          .toLowerCase();
        return tokens.every((token) => haystack.includes(token));
      })
      .slice(0, 30);
  }, [locale, nameFormat, persons, query]);

  function select(person: Person) {
    onChange(person.id);
    setQuery(formatPersonName(person, nameFormat));
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      {!hideLabel && <span className="label">{label}</span>}
      <div className={`relative ${hideLabel ? '' : 'mt-1'}`}>
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-heritage-400">
          <Icon name="search" className="h-4 w-4" />
        </span>
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onFocus={(event) => {
            setOpen(true);
            event.currentTarget.select();
          }}
          onBlur={() => {
            window.setTimeout(() => {
              if (
                containerRef.current &&
                !containerRef.current.contains(document.activeElement)
              ) {
                setOpen(false);
                setQuery(selectedName);
              }
            }, 120);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              setQuery(selectedName);
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActiveIndex((index) =>
                Math.min(index + 1, Math.max(filtered.length - 1, 0)),
              );
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' && open) {
              event.preventDefault();
              const person = filtered[activeIndex];
              if (person) select(person);
            }
          }}
          className="input py-2 pl-9 pr-8"
          placeholder={placeholder ?? t('Найти персону…')}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
              setOpen(false);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-heritage-400 hover:bg-heritage-100 hover:text-heritage-700"
            aria-label={t('Очистить')}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-heritage-100 bg-surface py-1 shadow-xl">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-heritage-500">
              {t('Никого не найдено')}
            </p>
          ) : (
            filtered.map((person, index) => {
              const name = formatPersonName(person, nameFormat);
              return (
                <button
                  key={person.id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(person)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`block w-full truncate px-3 py-2 text-left text-sm ${
                    index === activeIndex
                      ? 'bg-heritage-100 text-heritage-900'
                      : 'text-heritage-700 hover:bg-heritage-50'
                  }`}
                >
                  {name}
                  {person.birthYear && (
                    <span className="ml-2 text-xs text-heritage-400">
                      {person.birthYear}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
